import React from "react";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { PageKey } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";
import { OverviewCounts } from "../../common/requests/overview/endpoints";

export type BadgeTone = "attention" | "danger" | "quiet";

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Any one of these grants the item; omit for every admin. */
  permission?: PageKey | PageKey[];
  superadminOnly?: boolean;
  badge?: (counts: OverviewCounts) => { value: number; tone: BadgeTone; label: string } | null;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

const nonZero = (value: number | null | undefined, tone: BadgeTone, label: string) =>
  value ? { value, tone, label: `${value} ${label}` } : null;

// The groups are the owner's (kept as they were before the redesign; Marketing
// added 2026-10-13 for Promotions). Reports
// and their actions live together: Sales includes creating orders; Users &
// Sellers includes applications and messages.
export const NAV_SECTIONS: NavSection[] = [
  { items: [{ label: "Overview", path: ROUTES.OVERVIEW, icon: <SpaceDashboardOutlinedIcon /> }] },
  {
    title: "Operations",
    items: [
      {
        label: "Sales",
        path: ROUTES.SALES,
        icon: <ShoppingCartOutlinedIcon />,
        permission: "sales",
        badge: (c) => nonZero(c.orders_attention, "attention", "orders need attention"),
      },
      {
        label: "Payouts",
        path: ROUTES.WITHDRAWALS,
        icon: <AccountBalanceWalletOutlinedIcon />,
        permission: "withdrawals",
        badge: (c) => nonZero(c.payouts_pending?.count, "attention", "payouts to pay"),
      },
      {
        label: "Chats",
        path: ROUTES.CHAT_MONITOR,
        icon: <ForumOutlinedIcon />,
        superadminOnly: true,
        badge: (c) => nonZero(c.chats_new_24h, "quiet", "new chats in 24h"),
      },
    ],
  },
  {
    title: "People",
    items: [
      {
        label: "Users & Sellers",
        path: ROUTES.USER_MANAGEMENT,
        icon: <PeopleOutlineIcon />,
        permission: ["registered_users", "seller_creation", "notifications"],
        badge: (c) => nonZero(c.applications_pending, "attention", "seller applications waiting"),
      },
    ],
  },
  {
    title: "Marketing",
    items: [
      {
        label: "Promotions",
        path: ROUTES.PROMOTIONS,
        icon: <LocalOfferOutlinedIcon />,
        superadminOnly: true,
        badge: (c) => nonZero(c.promotions_drafts, "quiet", "drafts"),
      },
    ],
  },
  {
    title: "Platform",
    items: [
      {
        label: "Platform",
        path: ROUTES.PLATFORM,
        icon: <MonitorHeartOutlinedIcon />,
        superadminOnly: true,
        badge: (c) => nonZero(c.services?.issues, "danger", "services with issues"),
      },
      { label: "Activity log", path: ROUTES.ACTIVITY, icon: <HistoryIcon />, superadminOnly: true },
      { label: "Emails", path: ROUTES.EMAILS, icon: <MailOutlineIcon />, superadminOnly: true },
      { label: "Search acronyms", path: ROUTES.SEARCH_ACRONYMS, icon: <ManageSearchIcon />, superadminOnly: true },
    ],
  },
];
